import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../utils/api";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";

export default function ProductDetails() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { addToCart } = useCart();

  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleAddToCart = async () => {
    // Check authentication
    if (!isAuthenticated) {
      alert("Please login first");
      navigate("/login");
      return;
    }

    // Check stock
    if (product.stock === 0) {
      alert("Product is out of stock");
      return;
    }

    if (qty > product.stock) {
      alert(`Only ${product.stock} items available`);
      return;
    }

    setLoading(true);

    const result = await addToCart(product._id, qty);

    setLoading(false);

    if (result.success) {
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);

      // Reset quantity after adding
      setQty(1);
    } else {
      alert(result.message || "Could not add to cart");
    }
  };

  useEffect(() => {
    api.getProduct(slug).then((res) => setProduct(res.data || res));
  }, [slug]);

  if (!product) {
    return <div className="text-center py-20 text-lg">Loading product...</div>;
  }

  return (
    <div className="max-w-6xl mx-auto p-6 grid grid-cols-1 md:grid-cols-2 gap-12">
      {/* ---------------- IMAGE SECTION ---------------- */}
      <div className="flex justify-center">
        <div className="w-full max-w-md overflow-hidden rounded-xl shadow">
          <img
            src={product.images?.[0]}
            alt={product.name}
            className="w-full rounded-xl hover:scale-105 transition-transform duration-300 object-cover"
          />
        </div>
      </div>

      {/* ---------------- PRODUCT INFO ---------------- */}
      <div>
        {/* Title */}
        <h1 className="text-4xl font-bold text-gray-900">{product.name}</h1>

        {/* Price */}
        <p className="text-3xl text-purple-600 font-semibold mt-4">
          ₹ {product.price.toLocaleString()}
        </p>

        {/* Stars (static for now) */}
        <div className="flex items-center gap-1 mt-2 text-yellow-500">
          ⭐ ⭐ ⭐ ⭐ ☆
          <span className="text-gray-600 text-sm">(42 reviews)</span>
        </div>

        {/* Description */}
        <p className="mt-6 text-gray-700 leading-relaxed text-lg">
          {product.description}
        </p>

        {/* Info List */}
        <div className="mt-6 space-y-2 text-gray-700 text-sm">
          <p><strong>Category:</strong> {product.category}</p>
          {product.size && <p><strong>Size:</strong> {product.size}</p>}
          {product.packSize && <p><strong>Pack Size:</strong> {product.packSize}</p>}
        </div>

        {/* Customizable Badge */}
        {product.isCustomizable && (
          <div className="mt-6 p-4 bg-yellow-100 border-l-4 border-yellow-500 rounded">
            <p className="font-semibold text-yellow-700 text-lg">
              ✨ Customizable Product
            </p>
            <p className="text-sm">{product.customizationNote}</p>
          </div>
        )}

        {/* Stock */}
        <p className="mt-4 text-sm font-medium">
          {product.stock > 0 ? (
            <span className="text-green-600">
              ✔ In Stock ({product.stock} left)
            </span>
          ) : (
            <span className="text-red-600">❌ Out of Stock</span>
          )}
        </p>

        {/* Quantity Selector */}
        <div className="flex items-center gap-4 mt-6">
          <span className="font-semibold">Quantity:</span>

          <div className="flex items-center border rounded-lg overflow-hidden">
            <button
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
              onClick={() => qty > 1 && setQty(qty - 1)}
              disabled={loading}
            >
              -
            </button>

            <span className="px-6 py-2 font-semibold">{qty}</span>

            <button
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
              onClick={() => qty < product.stock && setQty(qty + 1)}
              disabled={loading}
            >
              +
            </button>
          </div>
        </div>

        {/* Add to Cart Button */}
        <button
          onClick={handleAddToCart}
          disabled={product.stock === 0 || loading}
          className="bg-purple-600 text-white w-full py-4 rounded-lg mt-8 text-lg font-semibold hover:bg-purple-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed relative"
        >
          {loading ? "Adding to Cart..." : "Add to Cart 🛒"}
        </button>

        {/* Success Message */}
        {showSuccess && (
          <div className="mt-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg text-center animate-fade-in">
            ✓ Added {qty} item(s) to cart!
          </div>
        )}

        {/* View Cart Button */}
        {showSuccess && (
          <button
            onClick={() => navigate("/cart")}
            className="w-full mt-3 border-2 border-purple-600 text-purple-600 py-3 rounded-lg hover:bg-purple-50 transition font-semibold"
          >
            View Cart
          </button>
        )}
      </div>
    </div>
  );
}
